import React, { FC, useCallback, useState } from 'react'
import { Box, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import { ThumbUpAlt, ThumbUpAltOutlined, ThumbDownAlt, ThumbDownAltOutlined, Send } from '@mui/icons-material'
import { FeedbackRating } from '../../../../models/cleo.model'
import { sendMessageFeedback } from '../../../../services/cleo.service'
import { handlePromise } from '../../../../utils/handlePromisse'

type MessageFeedbackProps = {
    messageId: number
    initialRating?: FeedbackRating
    onRated?: (rating: FeedbackRating) => void
}

const MessageFeedback: FC<MessageFeedbackProps> = ({ messageId, initialRating, onRated }) => {
    const [rating, setRating] = useState<FeedbackRating | null>(initialRating ?? null)
    const [comment, setComment] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [commentSent, setCommentSent] = useState(false)

    const submit = useCallback(async (value: FeedbackRating, text?: string) => {
        setSubmitting(true)
        const [, error] = await handlePromise(sendMessageFeedback(messageId, value, text))
        setSubmitting(false)
        return !error
    }, [messageId])

    const handleRate = useCallback(async (value: FeedbackRating) => {
        if (submitting || value === rating) return
        const ok = await submit(value)
        if (ok) {
            setRating(value)
            onRated?.(value)
        }
    }, [submitting, rating, submit, onRated])

    const handleSendComment = useCallback(async () => {
        if (!rating || !comment.trim() || submitting) return
        const ok = await submit(rating, comment.trim())
        if (ok) setCommentSent(true)
    }, [rating, comment, submitting, submit])

    return (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                    {rating == null ? 'Esta resposta foi útil?' : 'Obrigado pelo feedback!'}
                </Typography>
                <Tooltip title="Útil" arrow placement="top">
                    <span>
                        <IconButton size="small" disabled={submitting} onClick={() => handleRate(3)}>
                            {rating === 3
                                ? <ThumbUpAlt sx={{ fontSize: 16 }} color="success" />
                                : <ThumbUpAltOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />}
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Não útil" arrow placement="top">
                    <span>
                        <IconButton size="small" disabled={submitting} onClick={() => handleRate(1)}>
                            {rating === 1
                                ? <ThumbDownAlt sx={{ fontSize: 16 }} color="error" />
                                : <ThumbDownAltOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {rating != null && !commentSent && (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
                    <TextField
                        size="small"
                        variant="outlined"
                        placeholder="Conte o porquê (opcional)"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        multiline
                        maxRows={3}
                        fullWidth
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault()
                                handleSendComment()
                            }
                        }}
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                    />
                    <Tooltip title="Enviar comentário" arrow placement="top">
                        <span>
                            <IconButton size="small" color="primary" disabled={!comment.trim() || submitting} onClick={handleSendComment}>
                                <Send sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            )}

            {commentSent && (
                <Typography variant="caption" color="success.main">Comentário enviado.</Typography>
            )}
        </Box>
    )
}

export default MessageFeedback
